import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface ConversationRow {
  session_id: string;
  started?: string | null;
  last_seen?: string | null;
  bot_id?: string | null;
  bot_name?: string | null;
  course_id?: string | null;
  course_name?: string | null;
  turns: number;
  thumbs_up: number;
  thumbs_down: number;
  thumbs_down_with_text: number;
  measured: number;
  ungrounded: number;
  first_question?: string | null;
  /** Die zuletzt geschriebene Begründung einer negativen Bewertung. */
  feedback_text?: string | null;
}

export interface ConversationFacet {
  id: string;
  name?: string | null;
  count: number;
}

export interface ConversationListResponse {
  data: ConversationRow[];
  total: number;
  bots: ConversationFacet[];
  courses: ConversationFacet[];
}

export interface ConversationMessage {
  role: string;
  text: string;
  ts?: string | null;
  rating?: 'up' | 'down' | null;
  feedback_text?: string | null;
  grounded?: boolean | null;
  retrieval_hits?: number | null;
  /** analytics turn id — "Daraus lernen" drafts from it; null for the
   *  checkpointer fallback */
  event_id?: string | null;
  /** learnings already made from this turn */
  learning_ids?: string[];
}

export interface ConversationDetail {
  session_id: string;
  started?: string | null;
  last_seen?: string | null;
  bot_id?: string | null;
  bot_name?: string | null;
  course_id?: string | null;
  course_name?: string | null;
  turns: number;
  messages: ConversationMessage[];
  source: 'analytics' | 'checkpointer';
  available: boolean;
}

export type ConversationRating = 'any' | 'down' | 'down_with_text' | 'up';

/**
 * Konversationen aus den Analytics-Ereignissen.
 *
 * Der Verlauf kommt aus denselben Turn-Ereignissen wie die Liste, nicht aus
 * dem LangGraph-Checkpointer — deshalb ist er so lange lesbar wie die
 * Analytics-Aufbewahrung reicht und lässt sich durchsuchen. Der Checkpointer
 * bleibt Rückfall für alte Sitzungen ohne Turn-Ereignisse; welcher Weg es war,
 * steht in `source`.
 */
@Injectable({ providedIn: 'root' })
export class ConversationsService {
  private baseUrl = `${environment.tutorsBackend}`;

  constructor(private http: HttpClient) {}

  list(opts: {
    days?: number;
    bot_id?: string | null;
    course_id?: string | null;
    rating?: ConversationRating;
    q?: string | null;
    sort?: 'recent' | 'thumbs_down';
    page?: number;
    limit?: number;
  }) {
    let params = new HttpParams();
    Object.entries(opts).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<ConversationListResponse>(
      `${this.baseUrl}/analytics/conversations`,
      { params }
    );
  }

  detail(sessionId: string) {
    return this.http.get<ConversationDetail>(
      `${this.baseUrl}/analytics/conversations/${sessionId}`
    );
  }
}
